import type {
  ProofreadingRuleEntry,
  RuleBasedErrorEntry,
  VariationSummaryItem,
  VariationSummaryPosition,
} from "@/lib/api/proofreading";
import type {
  AiProofreadingErrorEntry,
  AiProofreadingPositionedEntry,
  AiProofreadingResultPayload,
  ForeshadowCheckEntry,
  ForeshadowCheckPositionedEntry,
  ResolvedProofreadingBatchChecks,
} from "@/lib/api/proofreadingAi";
import {
  AI_PROOFREADING_CHECK_IDS,
  AI_PROOFREADING_ERROR_CODE_I18N,
  AI_PROOFREADING_RESULT_KEYS,
  type AiProofreadingCheckId,
} from "@/constants/proofreadingAiChecks";
import type { Manuscript } from "@/lib/models";
import type {
  IssueSegment,
  MarkItem,
  ProofreadingIssue,
  ProofreadingIssueCategory,
  ProofreadingIssueLocation,
  ProofreadingIssueStatus,
} from "@/types/proofreading";
import {
  getOriginStartLine,
  getOriginStartWord,
  getVariationOriginStartLine,
  getVariationOriginStartWord,
  type RuleBasedErrorEntryStored,
  type VariationSummaryPositionStored,
} from "@/lib/utils/proofreadingStoredTypes";
import {
  createProofreadingTextCache,
  lineWordToOffset,
  snippetMatchesAtLineWord,
  type ProofreadingTextCache,
} from "@/lib/utils/proofreadingPositions";

export { lineWordToOffset, offsetToLineWord } from "@/lib/utils/proofreadingPositions";

function buildDiffSegments(source: string, compare: string): IssueSegment[] {
  if (!source) return [];
  if (!compare || source === compare) {
    return [{ text: source, highlight: true }];
  }

  let prefixLen = 0;
  const maxPrefix = Math.min(source.length, compare.length);
  while (prefixLen < maxPrefix && source[prefixLen] === compare[prefixLen]) {
    prefixLen += 1;
  }

  let suffixLen = 0;
  while (
    suffixLen < source.length - prefixLen &&
    suffixLen < compare.length - prefixLen &&
    source[source.length - 1 - suffixLen] === compare[compare.length - 1 - suffixLen]
  ) {
    suffixLen += 1;
  }

  const segments: IssueSegment[] = [];
  const midStart = prefixLen;
  const midEnd = source.length - suffixLen;

  if (prefixLen > 0) {
    segments.push({ text: source.slice(0, prefixLen) });
  }
  if (midEnd > midStart) {
    segments.push({ text: source.slice(midStart, midEnd), highlight: true });
  }
  if (suffixLen > 0) {
    segments.push({ text: source.slice(midEnd) });
  }

  return segments.length > 0 ? segments : [{ text: source, highlight: true }];
}

export function buildIssueId(entry: RuleBasedErrorEntry | RuleBasedErrorEntryStored): string {
  const stored = entry as RuleBasedErrorEntryStored;
  return `${entry.manuscript_id}:${getOriginStartLine(stored)}:${getOriginStartWord(stored)}:${entry.error_code}`;
}

function resolveManuscriptTitle(manuscriptKey: string, manuscriptList: Manuscript[]): string {
  const found = manuscriptList.find((m) => m.key === manuscriptKey);
  return found?.title ?? manuscriptKey;
}

function buildRuleLabelMap(rules: ProofreadingRuleEntry[]): Map<string, ProofreadingRuleEntry> {
  return new Map(rules.map((rule) => [rule.rule, rule]));
}

type HighlightRange = {
  start: number;
  end: number;
  id: string;
};

function resolveExpectedSnippetText(
  entry: RuleBasedErrorEntryStored,
  issueStatuses?: Map<string, ProofreadingIssueStatus>,
): string {
  const issueId = buildIssueId(entry);
  if (
    issueStatuses?.get(issueId) === "applied"
    && entry.fix_text != null
    && entry.fix_text.length > 0
  ) {
    return entry.fix_text;
  }
  return entry.target_text;
}

function entryToHighlightRange(
  text: string,
  entry: RuleBasedErrorEntryStored,
  issueStatuses?: Map<string, ProofreadingIssueStatus>,
  textCache?: ProofreadingTextCache,
): HighlightRange | null {
  if (entry.coord_invalid) {
    return null;
  }
  const expectedText = resolveExpectedSnippetText(entry, issueStatuses);
  const start = lineWordToOffset(text, entry.start_line, entry.start_word, textCache);
  if (start === null) {
    return null;
  }
  if (!snippetMatchesAtLineWord(text, entry.start_line, entry.start_word, expectedText, textCache)) {
    return null;
  }
  return { start, end: start + expectedText.length, id: buildIssueId(entry) };
}

/**
 * ルールベース校正結果から LexicalEditor 用の MarkItem 一覧を組み立てる。
 */
export function buildMarkItemsForManuscript(
  text: string,
  results: RuleBasedErrorEntry[],
  manuscriptKey: string,
  options: { issueStatuses?: Map<string, ProofreadingIssueStatus> } = {}
): MarkItem[] {
  const issueStatuses = options.issueStatuses ?? new Map<string, ProofreadingIssueStatus>();
  const textCache = createProofreadingTextCache(text);

  return results
    .filter((entry) => entry.manuscript_id === manuscriptKey)
    .map((entry) => entryToHighlightRange(
      text,
      entry as RuleBasedErrorEntryStored,
      issueStatuses,
      textCache,
    ))
    .filter((range): range is HighlightRange => range != null)
    .map(({ id, start, end }) => ({
      id,
      start,
      end,
      status: issueStatuses.get(id) ?? "pending",
    }));
}

function buildPointIssue(
  entry: RuleBasedErrorEntry,
  ruleMeta: ProofreadingRuleEntry | undefined,
  manuscriptList: Manuscript[]
): ProofreadingIssue {
  const fixText = entry.fix_text ?? "";
  const hasFix = entry.fix_text != null && entry.fix_text.length > 0;

  return {
    id: buildIssueId(entry),
    episodeKey: entry.manuscript_id,
    episodeTitle: resolveManuscriptTitle(entry.manuscript_id, manuscriptList),
    line: entry.start_line,
    column: entry.start_word,
    text: entry.target_text,
    summary: entry.detail ?? ruleMeta?.description ?? ruleMeta?.label ?? entry.error_code,
    matchSegments: buildDiffSegments(entry.target_text, hasFix ? fixText : entry.target_text),
    suggestionSegments: hasFix ? buildDiffSegments(fixText, entry.target_text) : [],
    selected: hasFix,
    applied: false,
    fixable: hasFix,
  };
}

function buildVariationIssueId(item: VariationSummaryItem): string {
  return `variation:${item.variation_type}:${item.canonical}`;
}

export function buildVariationLocationId(
  item: VariationSummaryItem,
  position: VariationSummaryPosition,
  variantText: string
): string {
  const stored = position as VariationSummaryPositionStored;
  return `variation:${item.variation_type}:${item.canonical}:${position.manuscript_id}:${getVariationOriginStartLine(stored)}:${getVariationOriginStartWord(stored)}:${variantText}`;
}

function buildVariationLocations(
  item: VariationSummaryItem,
  manuscriptList: Manuscript[]
): ProofreadingIssueLocation[] {
  const manuscriptOrder = new Map(item.manuscript_ids.map((id, index) => [id, index]));
  const locations: ProofreadingIssueLocation[] = [];

  item.variants.forEach((variant) => {
    (variant.position ?? []).forEach((pos) => {
      if ((pos as VariationSummaryPositionStored).coord_invalid) return;
      locations.push({
        id: buildVariationLocationId(item, pos, variant.text),
        episodeKey: pos.manuscript_id,
        episodeTitle: resolveManuscriptTitle(pos.manuscript_id, manuscriptList),
        line: pos.start_line,
        column: pos.start_word,
        text: variant.text,
      });
    });
  });

  locations.sort((a, b) => {
    const manuscriptDiff = (manuscriptOrder.get(a.episodeKey) ?? 0) - (manuscriptOrder.get(b.episodeKey) ?? 0);
    if (manuscriptDiff !== 0) return manuscriptDiff;
    return a.line - b.line || a.column - b.column;
  });

  return locations;
}

/**
 * 表記揺れ集計の各 position から LexicalEditor 用の MarkItem 一覧を組み立てる。
 */
export function buildVariationMarkItemsForManuscript(
  text: string,
  variationItems: VariationSummaryItem[],
  manuscriptKey: string,
  options: { issueStatuses?: Map<string, ProofreadingIssueStatus> } = {}
): MarkItem[] {
  const issueStatuses = options.issueStatuses ?? new Map<string, ProofreadingIssueStatus>();
  const textCache = createProofreadingTextCache(text);
  const marks: MarkItem[] = [];

  variationItems.forEach((item) => {
    item.variants.forEach((variant) => {
      (variant.position ?? []).forEach((pos) => {
        if (pos.manuscript_id !== manuscriptKey) return;
        if ((pos as VariationSummaryPositionStored).coord_invalid) return;
        const start = lineWordToOffset(text, pos.start_line, pos.start_word, textCache);
        if (start === null) return;
        if (!snippetMatchesAtLineWord(text, pos.start_line, pos.start_word, variant.text, textCache)) return;
        const id = buildVariationLocationId(item, pos, variant.text);
        marks.push({
          id,
          start,
          end: start + variant.text.length,
          status: issueStatuses.get(id) ?? "pending",
        });
      });
    });
  });

  return marks;
}

function buildVariationIssues(
  items: VariationSummaryItem[],
  activeManuscriptKey: string | undefined,
  ruleLabelMap: Map<string, ProofreadingRuleEntry>,
  manuscriptList: Manuscript[]
): ProofreadingIssueCategory[] {
  const categoryMap = new Map<string, ProofreadingIssueCategory>();

  items.forEach((item) => {
    if (activeManuscriptKey && !item.manuscript_ids.includes(activeManuscriptKey)) return;

    const ruleMeta = ruleLabelMap.get(item.variation_type);
    const locations = buildVariationLocations(item, manuscriptList);
    const firstLocation = locations[0];
    const fallbackManuscriptKey = item.manuscript_ids[0];
    const issue: ProofreadingIssue = {
      id: buildVariationIssueId(item),
      episodeKey: firstLocation?.episodeKey ?? fallbackManuscriptKey,
      episodeTitle: resolveManuscriptTitle(firstLocation?.episodeKey ?? fallbackManuscriptKey, manuscriptList),
      line: firstLocation?.line ?? 0,
      column: firstLocation?.column ?? 0,
      text: item.canonical,
      summary: "",
      matchSegments: [],
      suggestionSegments: [],
      selected: false,
      applied: true,
      fixable: false,
      variationVariants: item.variants.map(({ text, count }) => ({ text, count })),
      ...(locations.length > 0 ? { locations } : {}),
    };

    const existing = categoryMap.get(item.variation_type);
    if (existing) {
      existing.issues.push(issue);
    } else {
      categoryMap.set(item.variation_type, {
        id: item.variation_type,
        label: ruleMeta?.label ?? item.variation_type,
        issues: [issue],
      });
    }
  });

  return Array.from(categoryMap.values());
}

export function buildIssueCategories(
  results: RuleBasedErrorEntry[],
  variationItems: VariationSummaryItem[],
  rules: ProofreadingRuleEntry[],
  manuscriptList: Manuscript[],
  activeManuscriptKey?: string
): ProofreadingIssueCategory[] {
  const ruleLabelMap = buildRuleLabelMap(rules);
  const filteredResults = (activeManuscriptKey
    ? results.filter((entry) => entry.manuscript_id === activeManuscriptKey)
    : results
  ).filter((entry) => !(entry as RuleBasedErrorEntryStored).coord_invalid);

  const categoryMap = new Map<string, ProofreadingIssueCategory>();

  filteredResults.forEach((entry) => {
    const ruleMeta = ruleLabelMap.get(entry.error_code);
    const categoryId = entry.error_code;
    const existing = categoryMap.get(categoryId);
    const issue = buildPointIssue(entry, ruleMeta, manuscriptList);

    if (existing) {
      existing.issues.push(issue);
    } else {
      categoryMap.set(categoryId, {
        id: categoryId,
        label: ruleMeta?.label ?? entry.error_code,
        issues: [issue],
      });
    }
  });

  const variationCategories = buildVariationIssues(variationItems, activeManuscriptKey, ruleLabelMap, manuscriptList);

  variationCategories.forEach((variationCategory) => {
    const existing = categoryMap.get(variationCategory.id);
    if (existing) {
      existing.issues.push(...variationCategory.issues);
    } else {
      categoryMap.set(variationCategory.id, variationCategory);
    }
  });

  return Array.from(categoryMap.values());
}

export type IssueSortPosition = {
  episodeKey: string;
  line: number;
  column: number;
};

function buildManuscriptOrderMap(manuscriptKeys: string[]): Map<string, number> {
  return new Map(manuscriptKeys.map((key, index) => [key, index]));
}

function compareSortPositions(
  a: IssueSortPosition,
  b: IssueSortPosition,
  manuscriptOrder: Map<string, number>
): number {
  const manuscriptDiff =
    (manuscriptOrder.get(a.episodeKey) ?? Number.MAX_SAFE_INTEGER) -
    (manuscriptOrder.get(b.episodeKey) ?? Number.MAX_SAFE_INTEGER);
  if (manuscriptDiff !== 0) return manuscriptDiff;
  if (a.line !== b.line) return a.line - b.line;
  return a.column - b.column;
}

export function getIssueSortPosition(
  issue: ProofreadingIssue,
  manuscriptOrder: Map<string, number>
): IssueSortPosition {
  if (issue.locations?.length) {
    return issue.locations.reduce<IssueSortPosition>(
      (best, location) => {
        const current: IssueSortPosition = {
          episodeKey: location.episodeKey,
          line: location.line,
          column: location.column,
        };
        return compareSortPositions(current, best, manuscriptOrder) < 0 ? current : best;
      },
      {
        episodeKey: issue.locations[0].episodeKey,
        line: issue.locations[0].line,
        column: issue.locations[0].column,
      }
    );
  }

  if (issue.manuscriptLevel) {
    return {
      episodeKey: issue.episodeKey,
      line: Number.MAX_SAFE_INTEGER,
      column: Number.MAX_SAFE_INTEGER,
    };
  }

  return {
    episodeKey: issue.episodeKey,
    line: issue.line,
    column: issue.column,
  };
}

export function compareIssuesByManuscriptPosition(
  a: ProofreadingIssue,
  b: ProofreadingIssue,
  manuscriptOrder: Map<string, number>
): number {
  return compareSortPositions(
    getIssueSortPosition(a, manuscriptOrder),
    getIssueSortPosition(b, manuscriptOrder),
    manuscriptOrder
  );
}

export function flattenIssueCategories(
  categories: ProofreadingIssueCategory[],
  manuscriptKeys: string[]
): Array<{ categoryId: string; issue: ProofreadingIssue }> {
  const manuscriptOrder = buildManuscriptOrderMap(manuscriptKeys);
  const entries = categories.flatMap((category) =>
    category.issues.map((issue) => ({
      categoryId: category.id,
      issue,
    }))
  );

  return entries.sort((a, b) => compareIssuesByManuscriptPosition(a.issue, b.issue, manuscriptOrder));
}

function isPendingIssueStatus(issueId: string, issueStatuses: Map<string, ProofreadingIssueStatus>): boolean {
  const status = issueStatuses.get(issueId) ?? "pending";
  return status === "pending";
}

function countPointIssuesForManuscript(
  results: RuleBasedErrorEntry[],
  manuscriptKey: string,
  issueStatuses?: Map<string, ProofreadingIssueStatus>
): number {
  return results.filter((entry) => {
    if (entry.manuscript_id !== manuscriptKey) return false;
    if ((entry as RuleBasedErrorEntryStored).coord_invalid) return false;
    if (!issueStatuses) return true;
    return isPendingIssueStatus(buildIssueId(entry), issueStatuses);
  }).length;
}

export function countIssuesForManuscript(
  manuscriptKey: string,
  results: RuleBasedErrorEntry[],
  variationItems: VariationSummaryItem[],
  options: { issueStatuses?: Map<string, ProofreadingIssueStatus> } = {}
): number {
  const { issueStatuses } = options;
  const ruleBasedCount = countPointIssuesForManuscript(results, manuscriptKey, issueStatuses);
  let variationCount = 0;
  variationItems.forEach((item) => {
    item.variants.forEach((variant) => {
      (variant.position ?? []).forEach((pos) => {
        if (pos.manuscript_id !== manuscriptKey) return;
        if ((pos as VariationSummaryPositionStored).coord_invalid) return;
        if (issueStatuses && !isPendingIssueStatus(buildVariationLocationId(item, pos, variant.text), issueStatuses)) {
          return;
        }
        variationCount += 1;
      });
    });
  });
  return ruleBasedCount + variationCount;
}

export function pickIssueIdFromMarkIds(
  markIds: string[],
  results: RuleBasedErrorEntry[],
  appliedIssueIds: Set<string>
): string | null {
  let bestId: string | null = null;
  let bestRangeLen = Infinity;

  markIds.forEach((markId) => {
    if (appliedIssueIds.has(markId)) return;

    const entry = results.find((item) => buildIssueId(item) === markId);
    if (!entry) return;

    const rangeLen = entry.length;
    if (rangeLen < bestRangeLen) {
      bestId = markId;
      bestRangeLen = rangeLen;
    }
  });

  if (bestId) return bestId;

  return markIds.find((markId) => markId.startsWith("variation:") && !appliedIssueIds.has(markId)) ?? null;
}

const AI_PRIORITY_ORDER: Record<AiProofreadingErrorEntry["priority"], number> = {
  high: 0,
  middle: 1,
  low: 2,
  info: 3,
};

export type FlattenedAiResultEntry =
  | {
    checkId: Exclude<AiProofreadingCheckId, "foreshadowCheck">;
    entry: AiProofreadingErrorEntry;
  }
  | {
    checkId: "foreshadowCheck";
    entry: ForeshadowCheckEntry;
  };

function isOneBasedCoordinate(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1;
}

export function hasAiPointCoordinates(
  entry: Pick<AiProofreadingErrorEntry, "start_line" | "start_word">,
): entry is AiProofreadingPositionedEntry {
  return isOneBasedCoordinate(entry.start_line) && isOneBasedCoordinate(entry.start_word);
}

export function hasForeshadowCoordinates(
  entry: Pick<ForeshadowCheckEntry, "payoff_start_line">,
): entry is ForeshadowCheckPositionedEntry {
  return isOneBasedCoordinate(entry.payoff_start_line);
}

function countPrecedingManuscriptLevel(
  results: readonly object[],
  entry: object,
  isManuscriptLevel: (item: object) => boolean,
): number {
  const index = results.indexOf(entry);
  if (index < 0) return 0;
  return results.slice(0, index).filter((item) => isManuscriptLevel(item)).length;
}

export function buildAiIssueId(
  checkId: AiProofreadingCheckId,
  entry: AiProofreadingErrorEntry,
  results: readonly AiProofreadingErrorEntry[] = [entry],
): string {
  if (!hasAiPointCoordinates(entry)) {
    const ordinal = countPrecedingManuscriptLevel(
      results,
      entry,
      (item) => !hasAiPointCoordinates(item as AiProofreadingErrorEntry),
    );
    return `ai:${checkId}:${entry.manuscript_id}:manuscript:${entry.error_code}:${ordinal}`;
  }
  return `ai:${checkId}:${entry.manuscript_id}:${entry.start_line}:${entry.start_word}:${entry.error_code}`;
}

export function buildForeshadowIssueId(
  entry: ForeshadowCheckEntry,
  results: readonly ForeshadowCheckEntry[] = [entry],
): string {
  if (!hasForeshadowCoordinates(entry)) {
    const ordinal = countPrecedingManuscriptLevel(
      results,
      entry,
      (item) => !hasForeshadowCoordinates(item as ForeshadowCheckEntry),
    );
    return `ai:foreshadowCheck:${entry.payoff_manuscript_id}:manuscript:${entry.error_code}:${ordinal}`;
  }
  const itemPart = entry.item.replace(/\s+/g, " ").trim().slice(0, 40);
  return `ai:foreshadowCheck:${entry.payoff_manuscript_id}:${entry.payoff_start_line}:${entry.error_code}:${itemPart}`;
}

export function resolveAiIssueTypeLabel(
  errorCode: string,
  translate: (key: string) => string,
  typeFromApi?: string,
): string {
  const fromApi = typeFromApi?.trim();
  if (fromApi) {
    return fromApi;
  }
  const i18nKey = AI_PROOFREADING_ERROR_CODE_I18N[errorCode];
  if (i18nKey) {
    return translate(i18nKey);
  }
  return errorCode;
}

function buildAiPointIssue(
  entry: AiProofreadingErrorEntry,
  checkId: AiProofreadingCheckId,
  manuscriptList: Manuscript[],
  translateIssueType: (key: string) => string,
  results: readonly AiProofreadingErrorEntry[],
): ProofreadingIssue {
  const manuscriptLevel = !hasAiPointCoordinates(entry);
  const targetText = entry.target_text ?? "";
  const fixText = entry.fix_text ?? "";
  const hasFix = !manuscriptLevel && entry.fix_text != null && entry.fix_text.length > 0;

  return {
    id: buildAiIssueId(checkId, entry, results),
    episodeKey: entry.manuscript_id,
    episodeTitle: resolveManuscriptTitle(entry.manuscript_id, manuscriptList),
    line: hasAiPointCoordinates(entry) ? entry.start_line : 0,
    column: hasAiPointCoordinates(entry) ? entry.start_word : 0,
    text: targetText,
    manuscriptLevel,
    issueTypeLabel: resolveAiIssueTypeLabel(
      entry.error_code,
      translateIssueType,
      entry.type,
    ),
    aiPriority: entry.priority,
    summary: entry.comment ?? "",
    matchSegments: buildDiffSegments(targetText, hasFix ? fixText : targetText),
    suggestionSegments: hasFix ? buildDiffSegments(fixText, targetText) : [],
    selected: hasFix,
    applied: false,
    fixable: hasFix,
  };
}

function buildForeshadowIssue(
  entry: ForeshadowCheckEntry,
  manuscriptList: Manuscript[],
  translateIssueType: (key: string) => string,
  results: readonly ForeshadowCheckEntry[],
): ProofreadingIssue {
  const manuscriptLevel = !hasForeshadowCoordinates(entry);
  const targetText = entry.target_text ?? "";
  return {
    id: buildForeshadowIssueId(entry, results),
    episodeKey: entry.payoff_manuscript_id,
    episodeTitle: resolveManuscriptTitle(entry.payoff_manuscript_id, manuscriptList),
    line: hasForeshadowCoordinates(entry) ? entry.payoff_start_line : 0,
    column: hasForeshadowCoordinates(entry) ? 1 : 0,
    text: targetText,
    manuscriptLevel,
    issueTypeLabel: resolveAiIssueTypeLabel(
      entry.error_code,
      translateIssueType,
      entry.type,
    ),
    aiPriority: entry.priority,
    summary: entry.comment ?? "",
    matchSegments: targetText
      ? [{ text: targetText, highlight: true }]
      : [],
    suggestionSegments: [],
    selected: false,
    applied: false,
    fixable: false,
    foreshadow: {
      item: entry.item,
      plantsSeen: entry.plants_seen,
      axis: entry.axis,
      source: entry.source,
      storyChangesHeading: entry.story_changes_heading,
    },
  };
}

function sortAiEntries(entries: AiProofreadingErrorEntry[]): AiProofreadingErrorEntry[] {
  return [...entries].sort((a, b) => {
    const priorityDiff = AI_PRIORITY_ORDER[a.priority] - AI_PRIORITY_ORDER[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    const coordDiff = Number(!hasAiPointCoordinates(a)) - Number(!hasAiPointCoordinates(b));
    if (coordDiff !== 0) return coordDiff;
    if (!hasAiPointCoordinates(a) || !hasAiPointCoordinates(b)) return 0;
    if (a.start_line !== b.start_line) return a.start_line - b.start_line;
    return a.start_word - b.start_word;
  });
}

function sortForeshadowEntries(entries: ForeshadowCheckEntry[]): ForeshadowCheckEntry[] {
  return [...entries].sort((a, b) => {
    const priorityDiff = AI_PRIORITY_ORDER[a.priority] - AI_PRIORITY_ORDER[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    const coordDiff = Number(!hasForeshadowCoordinates(a)) - Number(!hasForeshadowCoordinates(b));
    if (coordDiff !== 0) return coordDiff;
    if (!hasForeshadowCoordinates(a) || !hasForeshadowCoordinates(b)) return 0;
    return a.payoff_start_line - b.payoff_start_line;
  });
}

function findTargetTextNearLine(
  text: string,
  line: number,
  targetText: string,
  radius = 2,
): { start: number; end: number } | null {
  if (!targetText || line < 1) return null;
  const cache = createProofreadingTextCache(text);
  const nearbyIndexes = Array.from({ length: radius }, (_, index) => index + 1)
    .flatMap((distance) => [line - 1 - distance, line - 1 + distance]);
  const lineIndexes = [line - 1, ...nearbyIndexes];

  return lineIndexes
    .filter((lineIndex) => lineIndex >= 0 && lineIndex < cache.lineStarts.length)
    .map((lineIndex) => {
      const lineStart = cache.lineStarts[lineIndex];
      const nextLineStart = cache.lineStarts[lineIndex + 1] ?? cache.normalized.length;
      const lineEnd = nextLineStart > lineStart && cache.normalized[nextLineStart - 1] === "\n"
        ? nextLineStart - 1
        : nextLineStart;
      const found = cache.normalized.slice(lineStart, lineEnd).indexOf(targetText);
      if (found < 0) return null;
      const start = lineStart + found;
      return { start, end: start + targetText.length };
    })
    .find((range): range is { start: number; end: number } => range != null) ?? null;
}

export function buildAiMarkItemsForManuscript(
  text: string,
  entries: FlattenedAiResultEntry[],
  manuscriptKey: string,
  options: { issueStatuses?: Map<string, ProofreadingIssueStatus> } = {},
): MarkItem[] {
  const issueStatuses = options.issueStatuses ?? new Map<string, ProofreadingIssueStatus>();

  return entries
    .filter((item) => item.checkId === "foreshadowCheck"
      ? item.entry.payoff_manuscript_id === manuscriptKey
      : item.entry.manuscript_id === manuscriptKey)
    .map((item) => {
      if (item.checkId === "foreshadowCheck") {
        if (!hasForeshadowCoordinates(item.entry)) return null;
        const range = findTargetTextNearLine(
          text,
          item.entry.payoff_start_line,
          item.entry.target_text ?? "",
        );
        if (range === null) return null;
        const id = buildForeshadowIssueId(item.entry);
        return {
          id,
          ...range,
          status: issueStatuses.get(id) ?? "pending",
        };
      }
      if (!hasAiPointCoordinates(item.entry)) return null;
      const id = buildAiIssueId(item.checkId, item.entry);
      const start = lineWordToOffset(text, item.entry.start_line, item.entry.start_word);
      if (start === null) return null;
      return {
        id,
        start,
        end: start + (item.entry.length ?? 0),
        status: issueStatuses.get(id) ?? "pending",
      };
    })
    .filter((mark): mark is MarkItem => mark != null);
}

export function buildAiIssueCategories(
  aiResult: AiProofreadingResultPayload,
  checkLabels: Record<AiProofreadingCheckId, string>,
  manuscriptList: Manuscript[],
  translateIssueType: (key: string) => string,
  activeManuscriptKey?: string,
): ProofreadingIssueCategory[] {
  const categories: ProofreadingIssueCategory[] = [];
  const checks = aiResult.checks as ResolvedProofreadingBatchChecks;

  AI_PROOFREADING_CHECK_IDS.forEach((checkId) => {
    if (!checks[checkId]) return;

    const resultKey = AI_PROOFREADING_RESULT_KEYS[checkId];
    if (checkId === "foreshadowCheck") {
      const rawResults = aiResult.result.foreshadow_check?.results ?? [];
      const filtered = activeManuscriptKey
        ? rawResults.filter((entry) => entry.payoff_manuscript_id === activeManuscriptKey)
        : rawResults;
      const issues = sortForeshadowEntries(filtered).map((entry) =>
        buildForeshadowIssue(entry, manuscriptList, translateIssueType, rawResults),
      );
      if (issues.length > 0) {
        categories.push({
          id: checkId,
          label: checkLabels[checkId],
          issues,
        });
      }
      return;
    }

    const rawResults = aiResult.result[resultKey]?.results ?? [];
    const filtered = activeManuscriptKey
      ? rawResults.filter((entry) => entry.manuscript_id === activeManuscriptKey)
      : rawResults;
    const issues = sortAiEntries(filtered).map((entry) =>
      buildAiPointIssue(entry, checkId, manuscriptList, translateIssueType, rawResults),
    );

    if (issues.length > 0) {
      categories.push({
        id: checkId,
        label: checkLabels[checkId],
        issues,
      });
    }
  });

  return categories;
}

export function collectManuscriptIdsFromAiResult(
  aiResult: AiProofreadingResultPayload | null,
): string[] {
  if (aiResult == null) return [];
  const ids = new Set<string>();
  flattenAiResultEntries(aiResult).forEach(({ entry }) => {
    ids.add(
      "payoff_manuscript_id" in entry
        ? entry.payoff_manuscript_id
        : entry.manuscript_id,
    );
  });
  return [...ids];
}

export function flattenAiResultEntries(
  aiResult: AiProofreadingResultPayload,
): FlattenedAiResultEntry[] {
  const checks = aiResult.checks as ResolvedProofreadingBatchChecks;
  const flattened: FlattenedAiResultEntry[] = [];

  AI_PROOFREADING_CHECK_IDS.forEach((checkId) => {
    if (!checks[checkId]) return;
    if (checkId === "foreshadowCheck") {
      (aiResult.result.foreshadow_check?.results ?? []).forEach((entry) => {
        flattened.push({ checkId, entry });
      });
      return;
    }
    const resultKey = AI_PROOFREADING_RESULT_KEYS[checkId];
    const results = aiResult.result[resultKey]?.results ?? [];
    results.forEach((entry) => {
      flattened.push({ checkId, entry });
    });
  });

  return flattened;
}

export function countAiIssuesForManuscript(
  manuscriptKey: string,
  aiResult: AiProofreadingResultPayload,
  options: { issueStatuses?: Map<string, ProofreadingIssueStatus> } = {},
): number {
  const { issueStatuses } = options;
  return flattenAiResultEntries(aiResult).filter((item) => {
    const matchesManuscript = item.checkId === "foreshadowCheck"
      ? item.entry.payoff_manuscript_id === manuscriptKey
      : item.entry.manuscript_id === manuscriptKey;
    if (!matchesManuscript) return false;
    if (!issueStatuses) return true;
    const issueId = item.checkId === "foreshadowCheck"
      ? buildForeshadowIssueId(item.entry, aiResult.result.foreshadow_check?.results ?? [])
      : buildAiIssueId(
        item.checkId,
        item.entry,
        aiResult.result[AI_PROOFREADING_RESULT_KEYS[item.checkId]]?.results ?? [],
      );
    const status = issueStatuses.get(issueId) ?? "pending";
    return status === "pending";
  }).length;
}
